import {Params} from '@angular/router';
import {
  CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX,
  CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX_LENGTH,
  CatalogSearchFilterQueryParamName,
  CatalogSearchFilterSelectOptions,
  SeparatedSearchParams
} from '@store/catalog/catalog.types';
import {RouterActions, RouterSelectors} from '@store/aviary/aviary.adapter';
import {isPopulatedArray, isPopulatedString, isUndefined} from '@aviary/store/common/typing.helpers';
import {createSelector} from '@ngrx/store';
import {MxSelectorCreator} from '@aviary/store/store.types';
import {CatalogSelectors} from '@store/catalog/catalog.selectors';
import {ListFilterSelectOptions} from '@aviary/store/common/common.types';

export function createFilterQueryParamNameFrom(filterName: string): CatalogSearchFilterQueryParamName {
  return `${CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX}${filterName}`;
}

export function createFilterParamUpdateActionFor(
  queryParamName: CatalogSearchFilterQueryParamName, selectedValues: string[]
): ReturnType<typeof RouterActions.addFilterParam> | ReturnType<typeof RouterActions.removeFilterParam> {
  return isPopulatedArray(selectedValues)
    ? RouterActions.addFilterParam(queryParamName, selectedValues.map(encodeURIComponent).join(',')) // Encode in case some value contains a comma.
    : RouterActions.removeFilterParam(queryParamName);
}

export function separateSearchFiltersFromQueryParams(params: Params): SeparatedSearchParams {
  return Object.entries(params).reduce<SeparatedSearchParams>(
    (accumulator, [key, value]) => {
      if (key.startsWith(CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX)) {
        accumulator.filters[key.slice(CATALOG_SEARCH_FILTER_QUERY_PARAM_PREFIX_LENGTH)] = convertFilterQueryParamValueToArray(value);
      } else {
        accumulator.otherQueryParams[key] = value;
      }

      return accumulator;
    },
    {
      filters: {},
      otherQueryParams: {}
    }
  );
}

export const filterOptionsWithSelectedFirstFor: MxSelectorCreator<[string], CatalogSearchFilterSelectOptions> =
  filterName => createSelector(
    CatalogSelectors.List.filterSelectOptionsFor(filterName),
    RouterSelectors.queryParamValue(createFilterQueryParamNameFrom(filterName)),
    (rawOptions, queryParamValue) =>
      sortFilterOptionsWithSelectedFirst(
        addSelectionStatusToFilterOptions(
          rawOptions,
          buildSelectedValuesFrom(queryParamValue)
        )
      )
  );

export function addSelectionStatusToFilterOptions(options: ListFilterSelectOptions, selectedValues: Set<string>): CatalogSearchFilterSelectOptions {
  return options.map(option => ({
    ...option,
    selected: selectedValues.has(option.value)
  }));
}

export function sortFilterOptionsWithSelectedFirst(options: CatalogSearchFilterSelectOptions): CatalogSearchFilterSelectOptions {
  return [
    ...options.filter(option => option.selected),
    ...options.filter(option => !option.selected)
  ];
}

export function buildSelectedValuesFrom(queryParamValue?: string): Set<string> {
  return new Set(convertFilterQueryParamValueToArray(queryParamValue));
}

export function convertFilterQueryParamValueToArray(value?: string): string[] {
  return isUndefined(value)
    ? []
    : value.split(',').map(decodeURIComponent).filter(isPopulatedString);  // Decode in case some value contains an encoded comma.
}
